import React, { useState, useEffect } from 'react';
import { 
  Button, 
  CircularProgress, 
  Pagination, 
  Alert, 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  Chip,
  useMediaQuery,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Fade
} from '@mui/material';
import { LocationOn, Edit, Delete, Add } from '@mui/icons-material';
import { useMovilidadElectrica } from '../../context/MovilidadElectricaContext';
import CreateParkerModal from './CreateParkerModal';
import EditParkerModal from './EditParkerModal';
import DeleteParkerModal from './DeleteParkerModal';

const GestionCeldas = () => {
  const { parkers, refreshParkers, loading, error } = useMovilidadElectrica();
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedParker, setSelectedParker] = useState(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  useEffect(() => {
    const placeId = sessionStorage.getItem("selectedPlaceId");
    const params = { page: currentPage, per_page: 10 };
    if (placeId) {
      params.place_id = placeId;
    }
    // Asumiendo que refreshParkers puede tomar parámetros para paginación
    refreshParkers(params);
  }, [currentPage, refreshParkers]);

  const openEditModal = (parker) => {
    setSelectedParker(parker);
    setIsEditModalOpen(true);
  };

  const openDeleteModal = (parker) => {
    setSelectedParker(parker);
    setIsDeleteModalOpen(true);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const CeldasCard = ({ parker }) => (
    <Fade in timeout={300}>
      <Card 
        sx={{ 
          height: '100%',
          borderRadius: { xs: 2, sm: 3 },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          border: '1px solid rgba(0, 38, 66, 0.08)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 40px rgba(0, 38, 66, 0.15)',
            borderColor: 'rgba(0, 38, 66, 0.2)'
          }
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: { xs: 1.5, sm: 2 },
            pb: { xs: 1.5, sm: 2 },
            borderBottom: '1px solid rgba(0, 38, 66, 0.08)'
          }}>
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                backgroundColor: 'rgba(0, 38, 66, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1.5
              }}
            >
              <LocationOn sx={{ 
                color: '#002642', 
                fontSize: { xs: '1.25rem', sm: '1.5rem' } 
              }} />
            </Box>
            <Typography 
              variant="h6" 
              component="h3" 
              sx={{ 
                fontWeight: 700,
                color: '#002642',
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}
            >
              Celda #{parker.parker_id}
            </Typography>
          </Box>
          
          <Typography 
            variant="body2" 
            sx={{ 
              mb: { xs: 1.5, sm: 2 },
              color: '#75787B',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              lineHeight: 1.5
            }}
          >
            <Box component="span" sx={{ fontWeight: 600, color: '#002642' }}>
              Ubicación:
            </Box>{' '}
            {parker.location}
          </Typography>
          
          <Box sx={{ mb: { xs: 2, sm: 2.5 } }}>
            <Chip 
              label="Activo" 
              color="success" 
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}
            />
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 1, sm: 1.5 }, 
            mt: 'auto'
          }}>
            <Button
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              startIcon={<Edit sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
              onClick={() => openEditModal(parker)}
              sx={{ 
                flex: 1,
                borderColor: '#002642',
                color: '#002642',
                fontWeight: 600,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                '&:hover': {
                  borderColor: '#00577A',
                  backgroundColor: 'rgba(0, 38, 66, 0.04)'
                }
              }}
            >
              Editar
            </Button>
            <Button
              variant="outlined"
              color="error"
              size={isMobile ? "small" : "medium"}
              startIcon={<Delete sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
              onClick={() => openDeleteModal(parker)}
              sx={{ 
                flex: 1,
                fontWeight: 600,
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}
            >
              Eliminar
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Fade>
  );

  return (
    <>
      <CreateParkerModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onParkerCreated={() => refreshParkers({ page: currentPage, per_page: 10 })}
      />
      <EditParkerModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        parker={selectedParker}
        onParkerUpdated={() => refreshParkers({ page: currentPage, per_page: 10 })}
      />
      <DeleteParkerModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        parker={selectedParker}
        onParkerDeleted={() => refreshParkers({ page: currentPage, per_page: 10 })}
      />

      <Box sx={{ mb: { xs: 2.5, sm: 4 } }}>
        <Button
          variant="contained"
          startIcon={<Add sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
          onClick={() => setIsCreateModalOpen(true)}
          sx={{
            backgroundColor: '#002642',
            color: '#FFFFFF',
            py: { xs: 1.25, sm: 2 },
            px: { xs: 2.5, sm: 4 },
            borderRadius: { xs: 2, sm: 3 },
            fontSize: { xs: '0.875rem', sm: '1rem' },
            fontWeight: 600,
            width: { xs: '100%', sm: 'auto' },
            minWidth: { sm: 200 },
            boxShadow: { xs: 2, sm: 3 },
            '&:hover': {
              backgroundColor: '#00577A',
              boxShadow: { xs: 3, sm: 6 },
              transform: { xs: 'none', sm: 'translateY(-2px)' }
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          Crear Nueva Celda
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error al cargar las celdas: {error.message}
        </Alert>
      ) : (
        <>
          {isMobile ? (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {(parkers.data || []).map((parker) => (
                <Grid item xs={12} sm={6} md={4} key={parker.parker_id}>
                  <CeldasCard parker={parker} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <TableContainer 
              component={Paper} 
              sx={{ 
                mb: 3,
                borderRadius: { xs: 2, sm: 3 },
                border: '1px solid rgba(0, 38, 66, 0.08)',
                boxShadow: '0 4px 20px rgba(0, 38, 66, 0.08)'
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ 
                    backgroundColor: '#002642',
                    '& .MuiTableCell-head': {
                      borderBottom: 'none'
                    }
                  }}>
                    <TableCell sx={{ 
                      color: 'white', 
                      fontWeight: 700,
                      fontSize: '1rem',
                      py: 2.5
                    }}>
                      ID de Celda
                    </TableCell>
                    <TableCell sx={{ 
                      color: 'white', 
                      fontWeight: 700,
                      fontSize: '1rem',
                      py: 2.5
                    }}>
                      Ubicación
                    </TableCell>
                    <TableCell sx={{ 
                      color: 'white', 
                      fontWeight: 700,
                      fontSize: '1rem',
                      py: 2.5
                    }}>
                      Estado
                    </TableCell>
                    <TableCell sx={{ 
                      color: 'white', 
                      fontWeight: 700,
                      fontSize: '1rem',
                      py: 2.5,
                      textAlign: 'center'
                    }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(parkers.data || []).map((parker, index) => (
                    <TableRow 
                      key={parker.parker_id}
                      sx={{ 
                        '&:hover': { 
                          backgroundColor: 'rgba(0, 38, 66, 0.04)',
                          '& .MuiTableCell-root': {
                            borderBottomColor: 'rgba(0, 38, 66, 0.2)'
                          }
                        },
                        transition: 'all 0.2s ease',
                        backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 38, 66, 0.02)'
                      }}
                    >
                      <TableCell sx={{ py: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            sx={{
                              p: 1,
                              borderRadius: 2,
                              backgroundColor: 'rgba(0, 38, 66, 0.08)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 1.5
                            }}
                          >
                            <LocationOn sx={{ 
                              color: '#002642', 
                              fontSize: '1.25rem'
                            }} />
                          </Box>
                          <Typography variant="body1" sx={{ 
                            fontWeight: 700,
                            color: '#002642',
                            fontSize: '1rem'
                          }}>
                            #{parker.parker_id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 2.5 }}>
                        <Typography variant="body1" sx={{ 
                          color: '#75787B',
                          fontSize: '0.95rem',
                          lineHeight: 1.4
                        }}>
                          {parker.location}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2.5 }}>
                        <Chip 
                          label="Activo" 
                          color="success" 
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            height: 32
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 2.5 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1.5, 
                          justifyContent: 'center'
                        }}>
                          <Button
                            variant="outlined"
                            size="medium"
                            startIcon={<Edit sx={{ fontSize: '1.125rem' }} />}
                            onClick={() => openEditModal(parker)}
                            sx={{
                              borderColor: '#002642',
                              color: '#002642',
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              px: 2.5,
                              py: 1,
                              minWidth: 100,
                              '&:hover': {
                                borderColor: '#00577A',
                                backgroundColor: 'rgba(0, 38, 66, 0.04)'
                              }
                            }}
                          >
                            Editar
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="medium"
                            startIcon={<Delete sx={{ fontSize: '1.125rem' }} />}
                            onClick={() => openDeleteModal(parker)}
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              px: 2.5,
                              py: 1,
                              minWidth: 100
                            }}
                          >
                            Eliminar
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {(parkers.data || []).length === 0 && (
            <Paper
              sx={{
                textAlign: 'center',
                py: { xs: 4, sm: 8 },
                px: { xs: 2, sm: 4 },
                borderRadius: { xs: 2, sm: 3 },
                border: '1px solid rgba(0, 38, 66, 0.08)',
                backgroundColor: 'rgba(0, 38, 66, 0.02)'
              }}
            >
              <Box
                sx={{
                  p: { xs: 2, sm: 3 },
                  borderRadius: { xs: 2, sm: 3 },
                  backgroundColor: 'rgba(0, 38, 66, 0.08)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: { xs: 2, sm: 3 }
                }}
              >
                <LocationOn sx={{ 
                  fontSize: { xs: '2.5rem', sm: '4rem' }, 
                  color: '#002642'
                }} />
              </Box>
              <Typography 
                variant="h5" 
                sx={{ 
                  color: '#002642',
                  fontWeight: 700,
                  mb: { xs: 1, sm: 1.5 },
                  fontSize: { xs: '1.25rem', sm: '1.5rem' }
                }}
              >
                No hay celdas registradas
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#75787B', 
                  mb: { xs: 3, sm: 4 },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  maxWidth: 400,
                  mx: 'auto',
                  lineHeight: 1.5
                }}
              >
                Comienza creando tu primera celda de estacionamiento eléctrico para administrar el sistema
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                onClick={() => setIsCreateModalOpen(true)}
                sx={{
                  backgroundColor: '#002642',
                  color: '#FFFFFF',
                  py: { xs: 1.5, sm: 2 },
                  px: { xs: 3, sm: 4 },
                  borderRadius: { xs: 2, sm: 3 },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: 600,
                  boxShadow: { xs: 2, sm: 3 },
                  '&:hover': {
                    backgroundColor: '#00577A',
                    boxShadow: { xs: 3, sm: 6 },
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                Crear Primera Celda
              </Button>
            </Paper>
          )}

          {(parkers.pages || 1) > 1 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mt: { xs: 3, sm: 4 },
              py: 2
            }}>
              <Pagination
                count={parkers.pages || 1}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? "small" : "large"}
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    fontWeight: 600,
                    '&.Mui-selected': {
                      backgroundColor: '#002642',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#00577A'
                      }
                    }
                  }
                }}
              />
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default GestionCeldas;
